// MessageTemplateEditorPane.js — Full-screen overlay editor for a single message template.
//
// Fixed inset-0 light surface (var(--surface-primary) / var(--text-primary)),
// matching the V2 email template editor. Two-column body: form on the left, a
// persistent token rail on the right (MessageTemplateTokenRail) on md+ screens.
// Fields: name (text input), body (plain-text textarea), MessageTemplateTokenPicker.
// Soft character-count hint (D-07) and a delete-confirm modal.
//
// NO rich HTML editor, NO folder picker, NO audience field, NO AI generator (D-02, D-03).
// Reserved fields (waContentSid, mediaUrl, rcsContentSid) are hidden from the UI entirely
// and round-trip as null through the save handler (D-09, MTPL-05).
//
// Props:
//   workspaceId  {string}         — authenticated workspace ID
//   template     {object|null}    — template to edit; null/undefined = new template
//   onClose      {Function}       — () => void — dismisses the overlay
//   onSaved      {Function}       — (template) => void — called after successful save
//   onDeleted    {Function}       — (templateId) => void — called after delete
//   showToast    {Function}       — (msg, type) => void — optional toast helper

import React from 'react';
import usePendingAction from '../../../hooks/usePendingAction';
import { Button } from '../../ui/primitives';
import MessageTemplateTokenPicker, { MessageTemplateTokenRail } from './MessageTemplateTokenPicker';
import {
    createMessageTemplate,
    updateMessageTemplate,
    deleteMessageTemplate,
} from '../../../services/messagingService';

// ─── Character count hint (D-07 — soft, no color change) ─────────────────────

function charCountHint(body = '') {
    const n = (body || '').length;
    if (n <= 160) return `${n} characters`;
    return `${n} characters — tokens expand at send time`;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function CloseIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" />
        </svg>
    );
}

// ─── Delete confirm modal ─────────────────────────────────────────────────────

function DeleteConfirmModal({ templateName, onConfirm, onCancel, deleting }) {
    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-msg-tmpl-title"
        >
            <div
                className="w-full max-w-sm mx-4 rounded-xl border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] shadow-xl flex flex-col"
            >
                <div className="px-6 pt-6 pb-4">
                    <h2
                        id="delete-msg-tmpl-title"
                        className="text-base font-semibold text-[color:var(--text-primary)]"
                    >
                        Delete &ldquo;{templateName}&rdquo;?
                    </h2>
                    <p className="text-sm text-[color:var(--text-muted)] mt-1">
                        This template will be permanently removed. This action cannot be undone.
                    </p>
                </div>
                <div className="flex items-center justify-end gap-2 px-6 pb-6">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={deleting}
                        className="inline-flex items-center h-9 px-4 rounded-md text-sm font-semibold border border-[color:var(--border-color)] bg-transparent hover:bg-[color:var(--surface-hover)] transition-colors disabled:opacity-50"
                        style={{ color: 'var(--text-primary)' }}
                    >
                        Cancel
                    </button>
                    <Button
                        variant="danger"
                        onClick={onConfirm}
                        loading={deleting}
                        loadingLabel="Deleting…"
                    >
                        Delete template
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ─── MessageTemplateEditorPane ─────────────────────────────────────────────────

export default function MessageTemplateEditorPane({
    workspaceId,
    template = null,
    onClose,
    onSaved,
    onDeleted,
    showToast,
}) {
    const isNew = !template?.id;

    // ── Editor state ──────────────────────────────────────────────────────────
    const [name, setName] = React.useState(template?.name || '');
    const [description, setDescription] = React.useState(template?.description || '');
    const [body, setBody] = React.useState(template?.body || '');
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

    // Ref for insert-at-cursor on the body textarea
    const bodyTextareaRef = React.useRef(null);

    // ── Insert token at cursor ────────────────────────────────────────────────
    const handleInsertToken = React.useCallback((token) => {
        const ta = bodyTextareaRef.current;
        if (ta) {
            const value = ta.value ?? '';
            const start = ta.selectionStart ?? value.length;
            const end = ta.selectionEnd ?? start;
            setBody(value.slice(0, start) + token + value.slice(end));
            requestAnimationFrame(() => {
                ta.focus();
                const caret = start + token.length;
                ta.selectionStart = ta.selectionEnd = caret;
            });
            return;
        }
        // Fallback: append
        setBody((prev) => (prev ? `${prev} ${token}` : token));
    }, []);

    // ── Save handler ──────────────────────────────────────────────────────────
    const { run: handleSave, pending: saving } = usePendingAction(async () => {
        try {
            const payload = {
                name: name.trim(),
                description: description.trim(),
                body,
                updatedAt: Date.now(),
                // D-09 reserved fields — always null in v1, round-trip unchanged (MTPL-05)
                waContentSid: template?.waContentSid || null,
                mediaUrl: template?.mediaUrl || null,
                rcsContentSid: template?.rcsContentSid || null,
            };
            if (isNew) {
                payload.createdAt = Date.now();
                const ref = await createMessageTemplate(workspaceId, payload);
                onSaved?.({ id: ref.id, ...payload });
                showToast?.('Template created.', 'success');
            } else {
                await updateMessageTemplate(workspaceId, template.id, payload);
                onSaved?.({ ...template, ...payload });
                showToast?.('Template saved.', 'success');
            }
            onClose?.();
        } catch (err) {
            showToast?.(
                err?.message || "Couldn't save your template. Check your connection and try again.",
                'error'
            );
        }
    });

    // ── Delete handler ────────────────────────────────────────────────────────
    const { run: runDelete, pending: deleting } = usePendingAction(async () => {
        try {
            await deleteMessageTemplate(workspaceId, template.id);
            onDeleted?.(template.id);
            showToast?.('Template deleted.', 'success');
            onClose?.();
        } catch (err) {
            showToast?.(err?.message || "Couldn't delete the template. Try again.", 'error');
        } finally {
            setShowDeleteConfirm(false);
        }
    });
    const handleConfirmDelete = () => (isNew ? undefined : runDelete());

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            {/* Full-screen overlay */}
            <div
                className="fixed inset-0 z-50 flex flex-col bg-[color:var(--surface-primary)]"
                style={{ color: 'var(--text-primary)' }}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between px-6 py-3 border-b border-[color:var(--border-color)] shrink-0"
                    style={{ background: 'var(--surface-muted)' }}
                >
                    <div>
                        <h2
                            className="text-base font-semibold leading-[1.3]"
                            style={{ color: 'var(--text-primary)' }}
                        >
                            {isNew ? 'New message template' : 'Edit message template'}
                        </h2>
                        {!isNew && template?.name && (
                            <p
                                className="text-xs mt-0.5"
                                style={{ color: 'var(--text-muted)' }}
                            >
                                {template.name}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close editor"
                        className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-[color:var(--surface-hover)] transition-colors"
                        style={{ color: 'var(--text-muted)' }}
                    >
                        <CloseIcon />
                    </button>
                </div>

                {/* Body — two columns: form (left) + persistent token rail (right) */}
                <div className="flex-1 flex min-h-0">
                  <div className="flex-1 min-w-0 overflow-y-auto px-6 py-5 flex flex-col gap-4">
                    {/* Name field */}
                    <div className="flex flex-col gap-1">
                        <label
                            htmlFor="msg-tmpl-name"
                            className="text-sm font-semibold"
                            style={{ color: 'var(--text-primary)' }}
                        >
                            Template name
                        </label>
                        <input
                            id="msg-tmpl-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Closing reminder"
                            className="h-9 px-3 rounded-md border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] text-sm focus:outline-none focus:ring-1 focus:ring-[color:var(--color-accent)]"
                            style={{ color: 'var(--text-primary)' }}
                        />
                    </div>

                    {/* Description field */}
                    <div className="flex flex-col gap-1">
                        <label
                            htmlFor="msg-tmpl-desc"
                            className="text-sm font-semibold"
                            style={{ color: 'var(--text-primary)' }}
                        >
                            Description
                            <span
                                className="ml-1 font-normal text-xs"
                                style={{ color: 'var(--text-muted)' }}
                            >
                                (optional)
                            </span>
                        </label>
                        <input
                            id="msg-tmpl-desc"
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Short note about when to use this template"
                            className="h-9 px-3 rounded-md border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] text-sm focus:outline-none focus:ring-1 focus:ring-[color:var(--color-accent)]"
                            style={{ color: 'var(--text-primary)' }}
                        />
                    </div>

                    {/* Body textarea + token picker */}
                    <div className="flex flex-col gap-1 flex-1">
                        <div className="flex items-center justify-between">
                            <label
                                htmlFor="msg-tmpl-body"
                                className="text-sm font-semibold"
                                style={{ color: 'var(--text-primary)' }}
                            >
                                Message body
                            </label>
                            {/* Popover picker — mobile only; the persistent rail handles md+ */}
                            <div className="md:hidden">
                                <MessageTemplateTokenPicker onInsert={handleInsertToken} />
                            </div>
                        </div>
                        <textarea
                            id="msg-tmpl-body"
                            ref={bodyTextareaRef}
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder="Hi {{client.firstName}}, your closing on {{transaction.propertyAddress}} is scheduled for {{transaction.closeDate}}."
                            rows={8}
                            className="w-full px-3 py-2 rounded-md border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] text-sm font-mono resize-y focus:outline-none focus:ring-1 focus:ring-[color:var(--color-accent)]"
                            style={{ color: 'var(--text-primary)' }}
                        />
                        {/* Soft character-count hint (D-07) */}
                        <p
                            className="text-xs"
                            style={{ color: 'var(--text-muted)' }}
                        >
                            {charCountHint(body)}
                        </p>
                    </div>
                  </div>

                  {/* Persistent token rail (md+ screens); popover above is the mobile fallback */}
                  <MessageTemplateTokenRail onInsert={handleInsertToken} />
                </div>

                {/* Footer */}
                <div
                    className="flex items-center justify-between px-6 py-4 border-t border-[color:var(--border-color)] shrink-0"
                    style={{ background: 'var(--surface-muted)' }}
                >
                    {/* Delete (existing templates only) */}
                    <div>
                        {!isNew && (
                            <button
                                type="button"
                                onClick={() => setShowDeleteConfirm(true)}
                                disabled={deleting}
                                className="inline-flex items-center h-9 px-3 rounded-md text-sm font-semibold transition-colors disabled:opacity-50"
                                style={{ color: 'var(--color-danger)' }}
                            >
                                Delete
                            </button>
                        )}
                    </div>

                    {/* Cancel + Save */}
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            className="inline-flex items-center h-9 px-4 rounded-md text-sm font-semibold border border-[color:var(--border-color)] bg-transparent hover:bg-[color:var(--surface-hover)] transition-colors disabled:opacity-50"
                            style={{ color: 'var(--text-primary)' }}
                        >
                            Cancel
                        </button>
                        <Button
                            variant="primary"
                            onClick={handleSave}
                            loading={saving}
                            loadingLabel="Saving…"
                            disabled={!name.trim()}
                        >
                            {isNew ? 'Create template' : 'Save template'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Delete confirmation */}
            {showDeleteConfirm && (
                <DeleteConfirmModal
                    templateName={template?.name || 'this template'}
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setShowDeleteConfirm(false)}
                    deleting={deleting}
                />
            )}
        </>
    );
}
